/**
 * GraduationRequestsTab — Admin view of pending graduation requests.
 * Shown as a dedicated tab in the cohort view for admins.
 *
 * When an admin clicks "Review", a modal loads the participant's full
 * activity completion details so the admin can make an informed decision.
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Container from '@cloudscape-design/components/container';
import Table from '@cloudscape-design/components/table';
import Header from '@cloudscape-design/components/header';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Modal from '@cloudscape-design/components/modal';
import FormField from '@cloudscape-design/components/form-field';
import Textarea from '@cloudscape-design/components/textarea';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Link from '@cloudscape-design/components/link';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import { cohortsApi, GraduationRequest } from '../../api/cohorts';
import { activitiesApi, CohortActivity, ActivityCompletion } from '../../api/activities';
import { queryKeys } from '../../lib/queryClient';
import { UserLink } from '../../components/UserLink';
import { useNotifications } from '../../components/NotificationFlashbar';

interface GraduationRequestsTabProps {
  cohortId: string;
}

interface ActivityWithCompletion extends CohortActivity {
  completion?: ActivityCompletion;
}

function CompletionStatusCell({ activity }: { activity: ActivityWithCompletion }) {
  const c = activity.completion;
  if (!c) {
    return <StatusIndicator type="error">Not completed</StatusIndicator>;
  }
  if (c.status === 'completed') {
    return (
      <SpaceBetween size="xxs">
        <StatusIndicator type="success">Completed</StatusIndicator>
        {c.completedAt && (
          <Box fontSize="body-s" color="text-body-secondary">
            {new Date(c.completedAt).toLocaleDateString()}
          </Box>
        )}
      </SpaceBetween>
    );
  }
  if (c.status === 'pending_approval') {
    return <StatusIndicator type="in-progress">Pending approval</StatusIndicator>;
  }
  return <StatusIndicator type="pending">Incomplete</StatusIndicator>;
}

export function GraduationRequestsTab({ cohortId }: GraduationRequestsTabProps) {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotifications();

  const [reviewingRequest, setReviewingRequest] = useState<GraduationRequest | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);

  // Fetch all graduation requests
  const { data, isLoading } = useQuery({
    queryKey: queryKeys.cohorts.graduationRequests(cohortId),
    queryFn: () => cohortsApi.listGraduationRequests(cohortId),
  });

  const pendingRequests = (data?.requests || []).filter(r => r.status === 'pending');

  // Fetch all cohort activities (needed for the review modal)
  const { data: activities } = useQuery({
    queryKey: queryKeys.activities.list(cohortId),
    queryFn: () => activitiesApi.list(cohortId),
    enabled: !!cohortId,
  });

  // Fetch the reviewing participant's completions when a request is opened
  const { data: completions, isLoading: completionsLoading } = useQuery({
    queryKey: queryKeys.activities.completions(reviewingRequest?.userId ?? '', cohortId),
    queryFn: () => activitiesApi.getUserCompletions(cohortId, reviewingRequest!.userId),
    enabled: !!reviewingRequest,
  });

  // Merge activities with completions for the modal
  const activitiesWithCompletion: ActivityWithCompletion[] = (activities || []).map(a => ({
    ...a,
    completion: (completions || []).find(c => c.activityId === a.activityId),
  }));

  const requiredActivities = activitiesWithCompletion.filter(a => a.requiredForGraduation);
  const otherActivities = activitiesWithCompletion.filter(a => !a.requiredForGraduation);

  const openReview = (request: GraduationRequest) => {
    setReviewingRequest(request);
    setReviewNotes('');
  };

  const closeReview = () => {
    setReviewingRequest(null);
    setReviewNotes('');
  };

  const handleReview = async (action: 'approve' | 'reject') => {
    if (!reviewingRequest) return;
    action === 'reject' ? setIsRejecting(true) : setIsSubmitting(true);
    try {
      if (action === 'approve') {
        await cohortsApi.approveGraduation(cohortId, reviewingRequest.userId, reviewNotes);
        showSuccess('Graduation approved', `${reviewingRequest.userName} has been graduated.`);
      } else {
        await cohortsApi.rejectGraduation(cohortId, reviewingRequest.userId, reviewNotes);
        showSuccess('Request rejected', `Graduation request for ${reviewingRequest.userName} has been rejected.`);
      }
      await queryClient.invalidateQueries({ queryKey: queryKeys.cohorts.graduationRequests(cohortId) });
      await queryClient.invalidateQueries({ queryKey: [...queryKeys.cohorts.detail(cohortId), 'participants'] });
      closeReview();
    } catch (err: any) {
      showError('Review failed', err.message || 'Could not process the graduation request.');
    } finally {
      setIsSubmitting(false);
      setIsRejecting(false);
    }
  };

  if (isLoading) {
    return <Container header={<Header variant="h2">Graduation Requests</Header>} />;
  }

  return (
    <>
      <Container>
        <Table
          header={
            <Header
              variant="h2"
              counter={pendingRequests.length > 0 ? `(${pendingRequests.length})` : '(0)'}
              description="Review and approve or reject participant graduation requests"
            >
              Graduation Requests
            </Header>
          }
          trackBy="userId"
          items={pendingRequests}
          variant="borderless"
          columnDefinitions={[
            {
              id: 'name',
              header: 'Participant',
              cell: (item) => <UserLink userName={item.userName} userId={item.userId} />,
              isRowHeader: true,
            },
            {
              id: 'requestedAt',
              header: 'Requested',
              cell: (item) => item.requestedAt
                ? new Date(item.requestedAt).toLocaleDateString()
                : '—',
              width: 130,
            },
            {
              id: 'actions',
              header: '',
              cell: (item) => (
                <Button
                  variant="primary"
                  onClick={() => openReview(item)}
                >
                  Review
                </Button>
              ),
              width: 120,
            },
          ]}
          empty={
            <Box textAlign="center" color="text-body-secondary" padding={{ vertical: 'l' }}>
              <Box variant="strong">No pending graduation requests</Box>
              <Box variant="p">Participants who have completed all required activities will appear here.</Box>
            </Box>
          }
        />
      </Container>

      {/* Rich review modal */}
      <Modal
        visible={!!reviewingRequest}
        onDismiss={() => { if (!isSubmitting && !isRejecting) closeReview(); }}
        size="large"
        header={`Review Graduation Request — ${reviewingRequest?.userName}`}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={closeReview} disabled={isSubmitting || isRejecting}>Cancel</Button>
              <Button
                loading={isRejecting}
                disabled={isSubmitting}
                onClick={() => handleReview('reject')}
              >
                Reject Request
              </Button>
              <Button
                variant="primary"
                loading={isSubmitting}
                disabled={isRejecting}
                onClick={() => handleReview('approve')}
              >
                Approve Graduation
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="l">
          {/* Summary row */}
          <ColumnLayout columns={2} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Participant</Box>
              <UserLink userName={reviewingRequest?.userName ?? reviewingRequest?.userId ?? ''} userId={reviewingRequest?.userId ?? ''} />
            </div>
            <div>
              <Box variant="awsui-key-label">Requested</Box>
              <Box>
                {reviewingRequest?.requestedAt
                  ? new Date(reviewingRequest.requestedAt).toLocaleDateString()
                  : '—'}
              </Box>
            </div>
          </ColumnLayout>

          {/* Required activities */}
          {completionsLoading ? (
            <Box color="text-body-secondary">Loading activity details...</Box>
          ) : (
            <SpaceBetween size="m">
              <Table
                header={
                  <Header
                    variant="h3"
                    description="All required activities must be completed before graduation can be approved"
                  >
                    Required Activities
                  </Header>
                }
                trackBy="activityId"
                items={requiredActivities}
                variant="borderless"
                columnDefinitions={[
                  {
                    id: 'title',
                    header: 'Activity',
                    cell: (item) => (
                      <SpaceBetween size="xxs">
                        <Box>{item.title}</Box>
                        {item.completion?.deliverableName && (
                          <Box fontSize="body-s" color="text-body-secondary">
                            {item.completion.deliverableName}
                          </Box>
                        )}
                        {item.completion?.deliverableLink && (
                          <Link external href={item.completion.deliverableLink} fontSize="body-s">
                            Deliverable
                          </Link>
                        )}
                        {item.completion?.recordingLink && (
                          <Link external href={item.completion.recordingLink} fontSize="body-s">
                            Recording
                          </Link>
                        )}
                      </SpaceBetween>
                    ),
                    isRowHeader: true,
                  },
                  {
                    id: 'type',
                    header: 'Type',
                    cell: (item) => item.activityType,
                    width: 120,
                  },
                  {
                    id: 'status',
                    header: 'Status',
                    cell: (item) => <CompletionStatusCell activity={item} />,
                    width: 160,
                  },
                ]}
                empty={<Box color="text-body-secondary">No required activities defined</Box>}
              />

              {otherActivities.length > 0 && (
                <Table
                  header={<Header variant="h3">Additional Activities</Header>}
                  trackBy="activityId"
                  items={otherActivities}
                  variant="borderless"
                  columnDefinitions={[
                    {
                      id: 'title',
                      header: 'Activity',
                      cell: (item) => (
                        <SpaceBetween size="xxs">
                          <Box>{item.title}</Box>
                          {item.completion?.deliverableName && (
                            <Box fontSize="body-s" color="text-body-secondary">
                              {item.completion.deliverableName}
                            </Box>
                          )}
                          {item.completion?.deliverableLink && (
                            <Link external href={item.completion.deliverableLink} fontSize="body-s">
                              Deliverable
                            </Link>
                          )}
                          {item.completion?.recordingLink && (
                            <Link external href={item.completion.recordingLink} fontSize="body-s">
                              Recording
                            </Link>
                          )}
                        </SpaceBetween>
                      ),
                      isRowHeader: true,
                    },
                    {
                      id: 'type',
                      header: 'Type',
                      cell: (item) => item.activityType,
                      width: 120,
                    },
                    {
                      id: 'status',
                      header: 'Status',
                      cell: (item) => <CompletionStatusCell activity={item} />,
                      width: 160,
                    },
                  ]}
                />
              )}
            </SpaceBetween>
          )}

          {/* Notes / feedback */}
          <FormField
            label="Notes (optional)"
            description="Add any feedback for the participant. They will be notified of the decision."
          >
            <Textarea
              value={reviewNotes}
              onChange={({ detail }) => setReviewNotes(detail.value)}
              placeholder="Any notes or feedback for the participant..."
              rows={3}
            />
          </FormField>
        </SpaceBetween>
      </Modal>
    </>
  );
}
